import { GOOGLE_KEY, overridePaperInputPlaceChanged } from '../../../helpers/api/google-api';
import { LitElement, css, html } from 'lit';
import { customElement, query } from 'lit/decorators';
import { delay, isDefined } from '../../../helpers/utils';

import { DEFAULT_ICON_SIZE } from '../../../helpers/constants';
import { RangeSliderComponent } from '../../range-slider-component';
import { StateController } from '../../../helpers/state/state-management/state-controller';
import { TextField } from '@material/mwc-textfield';
import { dispatchSettingsUpdate } from '../../../helpers/settings';
import { removeInertFromLocationElement } from '../../../helpers/inert';
import topicData from '../../../helpers/state/topic-data';

@customElement('map-center-scale')
export class MapCenterScale extends LitElement {

    @query('#defaultCenter')
    private defaultCenterElement: any & TextField;

	private readonly bindTopicState = new StateController(this, topicData);

    async firstUpdated(){
        while (this.defaultCenterElement === undefined) {
            await delay(10);
        }

        // Since we don't explicitly await the google key retrieval this technically
        // sets off a race condition. Practically, since the key retrieval is the first of
        // a couple API calls, it should not be a concern.
        this.defaultCenterElement.apiKey = GOOGLE_KEY;

        // Sort of hack to make the input not "red".
        this.defaultCenterElement._setInvalid(false);

        if (isDefined(this.defaultCenterElement)) {
            this.defaultCenterElement.value = topicData.topic?.default_map_center;
        }
    }

	onChangeDefaultMapCenter(e: Event) {
		const mapCenter = (e.target as HTMLInputElement).value;

        dispatchSettingsUpdate(
            {
				updatedValue: mapCenter,
				field: 'default_map_center',
                refreshPage: true
            },
            this
        );
	}

    onChangeDefaultMapZoom(e: Event) {
        const mapZoom = (e.target as RangeSliderComponent).value;

        dispatchSettingsUpdate(
            {
                updatedValue: mapZoom,
                field: 'default_map_zoom',
                refreshPage: true
            },
            this
        );
    };

    onChangeDefaultMapRotation(e: Event) {
        const mapRotation = (e.target as RangeSliderComponent).value;

        dispatchSettingsUpdate(
            {
                updatedValue: mapRotation,
                field: 'default_map_rotation',
                refreshPage: true
            },
            this
        );
    }

    onChangeDefaultIconSize(e: Event) {
        const iconSize = (e.target as RangeSliderComponent).value;

        dispatchSettingsUpdate(
            {
                updatedValue: iconSize,
                field: 'icon_size',
                refreshPage: true
            },
            this
        );
    };

    render() {
        return html`
            <h4 class="helpHeader">Map Center and Scale</h4>

            <paper-input-place
                id="defaultCenter"
                class="input"
                @value-changed="${this.onChangeDefaultMapCenter}"
                .searchBounds=${topicData.searchBounds}
                label="Default Map Center"
                @click="${removeInertFromLocationElement}"
                @api-loaded="${async () => await overridePaperInputPlaceChanged(this.defaultCenterElement)}"
                required>
            </paper-input-place>
            <br />

            <p>Default Map Zoom</p>

            <range-slider-component
                min=${1}
                minString="World"
                maxString="Street"
                max=${20}
                step=${1}
                .value=${topicData.topic?.default_map_zoom ?? 1}
                @change=${this.onChangeDefaultMapZoom}
            ></range-slider-component>
            <br />

            <p>Default Map Rotation</p>

            <range-slider-component
                min=${0}
                max=${360}
                step=${1}
                .value=${topicData.topic?.default_map_rotation ?? 0}
                @change=${this.onChangeDefaultMapRotation}
            ></range-slider-component>
            <br />

            <h4>Default Category Icon Size</h4>
            <p>Adjust the slider to change map icon sizes. Customize sizes for each category in 'Categories and Tags'. The default is 32.</p>

            <range-slider-component
                minString="Teeny Icons"
                maxString="Mega Icons"
                min=${10}
                max=${100}
                step=${1}
                unit=${''}
                .value=${topicData.topic?.icon_size ?? DEFAULT_ICON_SIZE}
                @change=${this.onChangeDefaultIconSize}
            ></range-slider-component>
        `;
    }

    static get styles() {
        return css`
            .slider-header {
                margin: 10px 0 0;
                color: var(--theme-lighter-text-color);
            }
        `;
    }
}
